<template>
  <UiSection id="rules" class="light">
    <div class="container flex-col mx-auto space-y-10">
      <h2 class="text-5xl font-extrabold" v-text="t('header')" />

      <div class="space-y-4">
        <h3 class="text-4xl font-extrabold" v-text="t('generalHeader')" />
        <div class="flex flex-col md:flex-row text-lg whitespace-normal">
          <div class="p-4 flex-1 rounded space-y-2 bg-gray-500 bg-opacity-25">
            <ul class="ml-5 list-disc text-base whitespace-normal" v-html="t('generalRules')" />
          </div>

          <div class="ml-4 p-4 flex items-center rounded space-x-4 text-md bg-gray-500 bg-opacity-25">
            <img class="h-20" src="../../assets/mouse.svg" alt />
            <div>
              <i class="fa fa-check-circle" style="color: #0b0" />&nbsp;{{ t('pickMap') }}
              <br />
              <i class="fa fa-times-circle" style="color: #c33" />&nbsp;{{ t('dropMap') }}
            </div>
            <Coin class="ml-8" />
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <h3 class="flex justify-between items-end text-4xl font-extrabold">
          {{ t('duelHeader') }}
          <small class="ml-4">
            <a href="https://docs.google.com/document/d/1BNtAe57OjkY3SgtvtKmAc-nQMk__M9IPKZpde-waUcM/edit" target="_blank">
              <i class="fa fa-file-alt mr-2" />
              <span>{{ t('duelFullRules') }}</span>
            </a>
          </small>
        </h3>

        <div class="p-4 rounded bg-gray-500 bg-opacity-25 text-lg whitespace-normal">
          <ul class="ml-5 list-disc text-base whitespace-normal" v-html="t('duelRules')" />
        </div>

        <div class="flex flex-wrap -ml-2 -mt-2 text-white font-bold" :class="{ selected }">
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-gradient-to-b from-gray-700 to-gray-900 hover:from-gray-600" @click="resetMaps(['1.1', '1.2', '1.3', '1.4', '1.5'])">
              <img class="w-full" src="../../assets/map_thumbnails/empty.png" alt />
              <figcaption class="absolute inset-0 py-2 px-3 flex flex-col justify-center text-center">
                <img class="mb-2 h-24 sm:h-0 md:h-16 xl:h-24" src="../../assets/game_modes/duel.svg" alt />
                <div class="text-xl">Duel</div>
                <small class="text-gray-600">
                  Double Elimination
                  <br />BO3, Grand Final — BO5
                </small>
              </figcaption>
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['1.1'] === 1, banned: maps['1.1'] === -1 }" @click="selectMap('1.1', 1)" @contextmenu.prevent="selectMap('1.1', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/duel_bioplant.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">Bioplant</figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['1.2'] === 1, banned: maps['1.2'] === -1 }" @click="selectMap('1.2', 1)" @contextmenu.prevent="selectMap('1.2', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/duel_frontier.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">Frontier</figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['1.3'] === 1, banned: maps['1.3'] === -1 }" @click="selectMap('1.3', 1)" @contextmenu.prevent="selectMap('1.3', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/duel_kasbah.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">Kasbah</figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['1.4'] === 1, banned: maps['1.4'] === -1 }" @click="selectMap('1.4', 1)" @contextmenu.prevent="selectMap('1.4', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/duel_perilous.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">Perilous</figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['1.5'] === 1, banned: maps['1.5'] === -1 }" @click="selectMap('1.5', 1)" @contextmenu.prevent="selectMap('1.5', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/duel_skybreak.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">Skybreak</figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <h3 class="flex justify-between items-end text-4xl font-extrabold">
          {{ t('draftHeader') }}
          <small class="ml-4">
            <a href="https://docs.google.com/document/d/15rpEZqton0kCaeQP2lyHNOG_Xi83io9LQ_90OrTM8lU/edit" target="_blank">
              <i class="fa fa-file-alt mr-2" />
              <span>{{ t('draftFullRules') }}</span>
            </a>
          </small>
        </h3>

        <div class="-ml-4 flex flex-col md:flex-row">
          <div class="ml-4 p-4 flex-1 rounded space-y-2 bg-gray-500 bg-opacity-25">
            <h4 class="text-xl font-extrabold" v-text="t('draftTeamHeader')" />
            <ul class="ml-5 list-disc text-base whitespace-normal" v-html="t('draftTeamRules')" />
          </div>
        </div>

        <div class="-ml-4 flex flex-col md:flex-row">
          <div class="ml-4 p-4 flex-1 rounded space-y-2 bg-gray-500 bg-opacity-25">
            <h4 class="text-xl font-extrabold" v-text="t('draftGroupHeader')" />
            <ul class="ml-5 list-disc text-base whitespace-normal" v-html="t('draftGroupRules')" />
          </div>
          <div class="ml-4 p-4 flex-1 rounded space-y-2 bg-gray-500 bg-opacity-25">
            <h4 class="text-xl font-extrabold" v-text="t('draftPlayoffHeader')" />
            <ul class="ml-5 list-disc text-base whitespace-normal" v-html="t('draftPlayoffRules')" />
          </div>
        </div>

        <div class="flex flex-wrap -ml-2 -mt-2 text-white font-bold" :class="{ selected }">
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-gradient-to-b from-gray-700 to-gray-900 hover:from-gray-600" @click="resetMaps(['2.1', '2.2', '2.3', '2.4', '2.5'])">
              <img class="w-full" src="../../assets/map_thumbnails/empty.png" alt />
              <figcaption class="absolute inset-0 py-2 px-3 flex flex-col justify-center text-center">
                <div class="mb-2 flex justify-center space-x-2">
                  <img class="h-24 sm:h-0 md:h-16 xl:h-24" src="../../assets/game_modes/wipeout.svg" alt />
                  <img class="h-24 sm:h-0 md:h-16 xl:h-24" src="../../assets/game_modes/macguffin.svg" alt />
                </div>
                <div class="text-xl">Wipeout 4v4 / MacGuffin 4v4</div>
                <small class="text-gray-600">
                  Groups: Round Robin — BO2<br />
                  Playoffs: Single Elimination — BO5
                </small>
              </figcaption>
            </figure>
          </div>

          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['2.1'] === 1, banned: maps['2.1'] === -1 }" @click="selectMap('2.1', 1)" @contextmenu.prevent="selectMap('2.1', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/mg_marina.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">
                Marina
                <small class="text-gray-500">(MacGuffin)</small>
              </figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['2.2'] === 1, banned: maps['2.2'] === -1 }" @click="selectMap('2.2', 1)" @contextmenu.prevent="selectMap('2.2', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/mg_sunken.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">
                Sunken
                <small class="text-gray-500">(MacGuffin)</small>
              </figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['2.3'] === 1, banned: maps['2.3'] === -1 }" @click="selectMap('2.3', 1)" @contextmenu.prevent="selectMap('2.3', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/wo_furnace.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">
                Furnace
                <small class="text-gray-500">(Wipeout)</small>
              </figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['2.4'] === 1, banned: maps['2.4'] === -1 }" @click="selectMap('2.4', 1)" @contextmenu.prevent="selectMap('2.4', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/wo_refinery.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">
                Refinery
                <small class="text-gray-500">(Wipeout)</small>
              </figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
          <div class="pl-2 pt-2 w-full sm:w-1/2 lg:w-1/3">
            <figure class="relative w-full overflow-hidden rounded cursor-pointer bg-black" :class="{ picked: maps['2.5'] === 1, banned: maps['2.5'] === -1 }" @click="selectMap('2.5', 1)" @contextmenu.prevent="selectMap('2.5', -1)">
              <img class="w-full" src="../../assets/map_thumbnails/wo_wellspring.png" alt />
              <figcaption class="absolute bottom-0 left-0 w-full py-3 px-4 bg-gradient-to-b from-transparent to-black">
                Wellspring
                <small class="text-gray-500">(Wipeout)</small>
              </figcaption>
              <i class="fa fa-times absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #f00" />
              <i class="fa fa-check absolute bottom-0 right-0 my-3 mx-4 text-6xl" style="color: #0c0" />
            </figure>
          </div>
        </div>
      </div>
    </div>
  </UiSection>
</template>

<script>
  import { computed, reactive } from 'vue'
  import { useI18n } from 'vue-i18n'
  import Coin from '../Coin.vue'
  import { menu } from '../../store.js'

  export default {
    name: 'SectionRules',
    components: { Coin },
    setup() {
      const maps = reactive({})

      const selected = computed(() => {
        return Object.values(maps).some(map => map)
      })

      function selectMap(id, value) {
        if (maps[id] === value) {
          maps[id] = 0
        } else {
          maps[id] = value
        }
      }

      function resetMaps(ids = []) {
        ids.forEach(id => (maps[id] = 0))
      }

      const { t } = useI18n({
        messages: {
          en: {
            header: 'Rules',
            pickMap: 'pick the map',
            dropMap: 'ban the map',
            generalHeader: 'General',
            generalRules: `
              <li>All matches are played on LAN or Moscow online server.</li>
              <li>All settings are default.</li>
              <li><b>All players must have unique avatar and sticker setup!</b>.</li>
              <li>All players will get a <b>DMTV Rating</b> based on their skills and achivements.</li>
            `,
            // generalDetails: 'The tournament brackets and player seeds will be revealed later...',
            duelHeader: 'Duel',
            duelFullRules: 'Full duel rules',
            duelRules: `
              <li>The players will be seeded based on their DMTV Rating.</li>
              <li>In <b>BO3</b> matches a <b>higher seed player</b> <b style="color: #0a0">picks</b> the first map, then the players <b style="color: #0a0">pick</b>, <b style="color: #c00">drop</b> and <b style="color: #c00">drop</b> the remaining maps one&nbsp;by&nbsp;one.</li>
              <li>In the <b>BO5</b> Grand Final match the <b>WB player</b> has <b>1:0</b> advantage and <b style="color: #c00">drops</b> one map, then the players <b style="color: #0a0">pick</b> the remaining maps one&nbsp;by&nbsp;one.</li>
            `,
            draftHeader: 'Draft',
            draftFullRules: 'Full draft rules',
            draftTeamHeader: 'Creating Teams',
            draftTeamRules: `
              <li>The top rank players become captains.</li>
              <li>
                The captains pick players for their teams following this rules:
                <ul class="ml-4 my-1 list-decimal text-sm">
                  <li>The highest rank captain <b style="color: #c00">bans</b> a player for the lowest rank captain;</li>
                  <li>The lowest rank captain <b style="color: #090">picks</b> one of the not banned players and <b style="color: #c00">bans</b> a player for the next picker with a higher rank;</li>
                  <li>The next captains do the same;</li>
                  <li>After the highest or lowest rank captains the order of picking becomes the opposite.</li>
                </ul>
              </li>
            `,
            draftGroupHeader: 'Group Stage',
            draftGroupRules: `
              <li>10 teams are split into <b>2 groups</b> (odd/even).</li>
              <li><b>Round robin</b> format, <b>best of 2</b>.</li>
              <li>The <b>higher seed captain</b> <b style="color: #0a0">picks</b> the first mode and map.</li>
              <li>The <b>first map loser</b> <b style="color: #0a0">picks</b> the next map from the <b>second game mode</b>.</li>
              <li>The <b>top 2</b> teams in each group advance to the Playoff.</li>
            `,
            draftPlayoffHeader: 'Playoff',
            draftPlayoffRules: `
              <li>4 teams seeded from their group placement: 1A — 2B, 1B — 2A.</li>
              <li><b>Single Elimination</b> format, <b>best of 5</b>.</li>
              <li>Map order: Furnace, Marina, Wellspring, Sunken, Refinery.</li>
            `,
          },
          ru: {
            header: 'Правила',
            pickMap: 'выбрать карту',
            dropMap: 'вычеркнуть карту',
            generalHeader: 'Общие',
            generalRules: `
              <li>Все матчи будут играться на LAN-серверах или московских онлайн-серверах.</li>
              <li>Все настройки матчей — стандартные.</li>
              <li><b>Все игроки обязаны использовать уникальный набор стикеров и аватар!</b></li>
              <li>Всем игрокам будет присвоен <b>рейтинг DMTV</b>, основанный на их навыках и достижениях.</li>
            `,
            // generalDetails: 'О турнирных сетках и посеве игроков будет рассказано позже...',
            duelHeader: 'Дуэль',
            duelFullRules: 'Полные правила дуэлей',
            duelRules: `
              <li>Игроки будут посеяны на основе их рейтинга DMTV.</li>
              <li>В <b>BO3</b> матчах <b>игрок с более высоким рейтингом</b> <b style="color: #0a0">выбирает</b> первую карту, затем игроки по очереди <b style="color: #0a0">выбирают</b>, <b style="color: #c00">вычеркивают</b> и снова <b style="color: #c00">вычеркивают</b> оставшиеся карты.</li>
              <li>В <b>BO5</b> матче гранд-финала <b>игрок из верхней сетки</b> имеет преимущество <b>1:0</b> и <b style="color: #c00">вычеркивает</b> одну карту, затем игроки по очереди <b style="color: #0a0">выбирают</b> оставшиеся карты.</li>
            `,
            draftHeader: 'Драфт',
            draftFullRules: 'Полные правила драфта',
            draftTeamHeader: 'Создание команд',
            draftTeamRules: `
              <li>Игроки с наивысшым рейтингом становятся капитанами.</li>
              <li>
                Капитаны выбирают игроков следующим образом:
                <ul class="ml-4 my-1 list-decimal text-sm">
                  <li>Капитан с <b>наивысшим</b> рейтингом <b style="color: #c00">банит</b> игрока для капитана с <b>наименьшим</b> рейтингом;</li>
                  <li>Капитан с <b>наименьшим</b> рейтингом <b style="color: #090">выбирает</b> одного из незабаненных игроков и <b style="color: #c00">банит</b> игрока для следующего капитана, имеющего более высокий рейтинг;</li>
                  <li>Следующие капитаны делают то же самое;</li>
                  <li>После капитанов с наибольшим или наименьшим рейтингом порядок выбора меняетя в обратную сторону.</li>
                </ul>
              </li>
            `,
            draftGroupHeader: 'Групповой этап',
            draftGroupRules: `
              <li>10 команд делятся на <b>2 группы</b> (четные/нечетные).</li>
              <li><b>Круговой</b> формат, <b>best of 2</b>.</li>
              <li>Капитан с более высоким рейтингом <b style="color: #0a0">выбирает</b> первый режим и карту.</li>
              <li>Капитан проигравшей команды <b style="color: #0a0">выбирает</b> следующую карту в <b>другом режиме</b>.</li>
              <li><b>Топ 2</b> команды из каждой группы проходят в плей офф.</li>
            `,
            draftPlayoffHeader: 'Плей офф',
            draftPlayoffRules: `
              <li>4 команды будут посеяны на основе занятого места: 1A — 2B, 1B — 2A.</li>
              <li><b>Олимпийская</b> система, <b>best of 5</b>.</li>
              <li>Порядок карт: Furnace, Marina, Wellspring, Sunken, Refinery.</li>
            `,
          },
        },
      })

      menu.rules = {
        name: computed(() => t('header')),
        icon: 'fa fa-gavel',
      }

      return { maps, selected, selectMap, resetMaps, t }
    },
  }
</script>

<style scoped>
  .list-decimal {
    counter-reset: item;
  }
  .list-decimal li {
    display: block;
    text-indent: -1.1em;
  }
  .list-decimal li:before {
    content: counters(item, '.') '. ';
    counter-increment: item;
  }

  figure > img.w-full {
    opacity: 0.8;
  }
  figure:hover > img.w-full {
    opacity: 1;
  }
  figure.picked > img {
    opacity: 0.9;
    filter: brightness(1.2);
  }
  figure.banned > img {
    opacity: 0.9;
    filter: grayscale(1);
  }
  .selected img.hidden {
    display: none !important;
  }

  figure > .fa-check,
  figure > .fa-times {
    display: none;
  }
  figure.picked > .fa-check {
    display: block;
  }
  figure.banned > .fa-times {
    display: block;
  }
</style>
